import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Row, Select,
  Space, Table, Tag, Typography, message,
} from 'antd';
import { CameraOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { video, cameraCalibrations } from '../../../services/api';
import { getStoredUser } from '../../../utils/auth';
import EzvizPlayer from '../../../components/EzvizPlayer';

function matrixFromFov(width, height, fov) {
  const focal = width / (2 * Math.tan((Number(fov) * Math.PI / 180) / 2));
  return [[focal, 0, width / 2], [0, focal, height / 2], [0, 0, 1]];
}

export default function VideoCalibration() {
  const isAdmin = getStoredUser().role === 'admin';
  const playerRef = useRef(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const [camera, setCamera] = useState(null);
  const [calibration, setCalibration] = useState(null);
  const [points, setPoints] = useState([]);
  const [frameUrl, setFrameUrl] = useState(null);
  const [pendingPixel, setPendingPixel] = useState(null);
  const [pendingScene, setPendingScene] = useState(null);
  const [assetCode, setAssetCode] = useState('');
  const [width, setWidth] = useState(1280);
  const [height, setHeight] = useState(720);
  const [fov, setFov] = useState(70);
  const [saving, setSaving] = useState(false);
  const [computing, setComputing] = useState(false);
  const [projectedPoints, setProjectedPoints] = useState([]);
  const [twinFocus, setTwinFocus] = useState('01');

  const selectedCamera = cameras.find(item => item.id === cameraId);

  const loadCalibration = useCallback(async (id) => {
    const res = await cameraCalibrations.get(id);
    const data = res.data;
    setCalibration(data.calibration);
    setPoints(data.points || []);
    if (data.calibration?.resolution_width) setWidth(data.calibration.resolution_width);
    if (data.calibration?.resolution_height) setHeight(data.calibration.resolution_height);
  }, []);

  useEffect(() => {
    video.list().then(res => {
      const list = res.data || [];
      setCameras(list);
      const pilot = list.find(item => item.channel_no === 25) || list[0];
      if (pilot) setCameraId(pilot.id);
    }).catch(err => message.error(err.message || '摄像头加载失败'));
  }, []);

  useEffect(() => {
    if (!cameraId) return;
    setCamera(cameras.find(item => item.id === cameraId) || null);
    setFrameUrl(null);
    setPendingPixel(null);
    setPendingScene(null);
    setProjectedPoints([]);
    loadCalibration(cameraId).catch(err => message.error(err.message || '标定数据加载失败'));
  }, [cameraId, cameras, loadCalibration]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'yushe-calibration-point' && Array.isArray(event.data.position)) {
        setPendingScene(event.data.position);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const captureFrame = () => {
    if (!playerRef.current) {
      message.warning('请先等待真实视频加载完成');
      return;
    }
    playerRef.current.capturePicture('calibration', (data) => {
      const base64 = data.base64 || data;
      setFrameUrl(base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`);
      setPendingPixel(null);
    });
  };

  const importFrame = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFrameUrl(reader.result);
      setPendingPixel(null);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const clickFrame = (event) => {
    if (!isAdmin) return;
    const image = event.currentTarget;
    const rect = image.getBoundingClientRect();
    setPendingPixel({
      x: (event.clientX - rect.left) * image.naturalWidth / rect.width,
      y: (event.clientY - rect.top) * image.naturalHeight / rect.height,
    });
  };

  const frameLoaded = (event) => {
    const image = event.currentTarget;
    if (image.naturalWidth && image.naturalHeight) {
      setWidth(image.naturalWidth);
      setHeight(image.naturalHeight);
    }
  };

  const addPoint = async () => {
    if (!pendingPixel || !pendingScene) {
      message.warning('请分别点击视频像素点和三维实景点');
      return;
    }
    try {
      setSaving(true);
      await cameraCalibrations.addPoint(cameraId, {
        asset_code: assetCode || null,
        pixel_x: pendingPixel.x,
        pixel_y: pendingPixel.y,
        scene_x: pendingScene[0],
        scene_y: pendingScene[1],
        scene_z: pendingScene[2],
        point_type: assetCode ? 'equipment' : 'static_feature',
      });
      await loadCalibration(cameraId);
      setProjectedPoints([]);
      setPendingPixel(null);
      setPendingScene(null);
      setAssetCode('');
      message.success('标定点已保存');
    } catch (err) {
      message.error(err.response?.data?.message || err.message || '保存标定点失败');
    } finally {
      setSaving(false);
    }
  };

  const deletePoint = async (point) => {
    try {
      await cameraCalibrations.deletePoint(point.id);
      await loadCalibration(cameraId);
      setProjectedPoints([]);
      message.success('标定点已删除');
    } catch (err) {
      message.error(err.message || '删除失败');
    }
  };

  const saveParams = async () => {
    await cameraCalibrations.updateParams(cameraId, {
      intrinsic_json: { camera_matrix: matrixFromFov(width, height, fov) },
      distortion_json: [0, 0, 0, 0, 0],
      resolution_width: width,
      resolution_height: height,
      notes: `水平视场角估计值 ${fov}°，如有镜头标定结果请替换`,
    });
  };

  const compute = async () => {
    try {
      setComputing(true);
      await saveParams();
      const res = await cameraCalibrations.compute(cameraId);
      setCalibration(res.data.calibration);
      setProjectedPoints(res.data.projected_points || []);
      const rms = res.data.calibration?.reprojection_rms;
      if (res.data.calibration?.status === 'verified') {
        message.success(`标定通过，RMS=${Number(rms).toFixed(3)} 像素`);
      } else {
        message.warning(`标定误差偏大，RMS=${Number(rms).toFixed(3)} 像素，请调整点位`);
      }
    } catch (err) {
      message.error(err.response?.data?.message || err.message || '标定计算失败');
    } finally {
      setComputing(false);
    }
  };

  const columns = [
    { title: '序号', dataIndex: 'sequence', width: 60 },
    { title: '资产', dataIndex: 'asset_code', render: value => value || '静态特征点' },
    { title: '视频像素', render: (_, p) => `${p.pixel_x.toFixed(1)}, ${p.pixel_y.toFixed(1)}` },
    { title: '三维坐标', render: (_, p) => `${p.scene_x.toFixed(2)}, ${p.scene_y.toFixed(2)}, ${p.scene_z.toFixed(2)}` },
    { title: '操作', width: 70, render: (_, point) => isAdmin && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => deletePoint(point)} /> },
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="单相机标定"
        description="先等待真实视频出现，再截取当前帧。视频与三维模型中点击同一实物，至少保存6组对应点，建议8—12组。标定点不要选车辆、人员或阴影。"
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>标定摄像头</span>
          <Select
            showSearch optionFilterProp="label" style={{ width: 240 }} value={cameraId} onChange={setCameraId}
            options={cameras.map(item => ({ value: item.id, label: `${item.name}（通道${item.channel_no}）` }))}
          />
          <Button icon={<CameraOutlined />} onClick={captureFrame} disabled={!cameraId}>截取当前帧</Button>
          <Button onClick={() => document.getElementById('calibration-frame-file')?.click()}>导入已截真实帧</Button>
          <input id="calibration-frame-file" type="file" accept="image/jpeg,image/png" onChange={importFrame} hidden />
          <span>水平视场角</span>
          <InputNumber value={fov} min={20} max={150} onChange={setFov} />°
          <span>分辨率</span>
          <InputNumber value={width} min={1} onChange={setWidth} style={{ width: 90 }} /> ×
          <InputNumber value={height} min={1} onChange={setHeight} style={{ width: 90 }} />
          <Tag color={calibration?.status === 'verified' ? 'green' : 'orange'}>{calibration?.status === 'verified' ? '标定通过' : '采集中'}</Tag>
          {calibration?.reprojection_rms != null && <Tag>RMS {Number(calibration.reprojection_rms).toFixed(3)} px</Tag>}
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card size="small" title="1. 点击真实视频中的实物">
            {frameUrl ? (
              <img
                src={frameUrl} alt="摄像头标定帧" onLoad={frameLoaded} onClick={clickFrame}
                style={{ width: '100%', cursor: 'crosshair', display: 'block', background: '#071222' }}
              />
            ) : <Empty description="请先截取真实视频帧" />}
            {pendingPixel && <Typography.Text type="success">已选像素：{pendingPixel.x.toFixed(1)}, {pendingPixel.y.toFixed(1)}</Typography.Text>}
          </Card>
          <Card size="small" title="实时摄像头" style={{ marginTop: 16 }}>
            {selectedCamera && <EzvizPlayer key={selectedCamera.id} camera={selectedCamera} onPlayer={player => { playerRef.current = player; }} />}
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card
            size="small" title="2. 点击三维模型中的同一实物"
            extra={<Select size="small" value={twinFocus} onChange={setTwinFocus} style={{ width: 130 }} options={Array.from({ length: 10 }, (_, index) => ({ value: String(index + 1).padStart(2, '0'), label: `聚焦${String(index + 1).padStart(2, '0')}号桩` }))} />}
          >
            <iframe
              key={twinFocus} src={`/twin/yushe/cesium.html?calibrate&focus=${twinFocus}`} title="三维标定"
              style={{ width: '100%', height: 520, border: 0, background: '#071222' }}
            />
            {pendingScene && <Typography.Text type="success">已取三维点：{pendingScene.map(v => v.toFixed(3)).join(', ')}</Typography.Text>}
          </Card>
          <Card size="small" title="3. 保存对应关系" style={{ marginTop: 16 }}>
            <Space wrap>
              <Input
                placeholder="关联资产编码（可选），如20260903000001"
                value={assetCode} onChange={event => setAssetCode(event.target.value)} style={{ width: 300 }}
              />
              <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={addPoint} disabled={!isAdmin}>添加标定点</Button>
              <Button type="primary" loading={computing} onClick={compute} disabled={!isAdmin || points.length < 6}>计算标定</Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card size="small" title={`标定点（${points.length}/24）`} style={{ marginTop: 16 }}>
        <Table rowKey="id" size="small" columns={columns} dataSource={points} pagination={false} scroll={{ x: 900 }} />
        {projectedPoints.length > 0 && <Typography.Text type="secondary">已返回 {projectedPoints.length} 个重投影点，可用于画面叠加复核。</Typography.Text>}
      </Card>
    </div>
  );
}
